// PartnerBrandingForm — the white-label branding fields for one partner org.
// Shared by the staff-facing dialog on the Partners page and the partner's own
// Settings → Marketing → Branding tab (Iter 244), so both edit exactly the
// same shape: { enabled, brand_name, brand_subtitle, primary_color, logo_url,
// logo_url_light, logo_url_dark, support_escalations }.
import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Image as ImageIcon, ExternalLink, UploadCloud, Loader2 } from "lucide-react";
import { TopBarPreview } from "@/components/TopBarPreview";
import { LoginBrandPreview } from "@/components/LoginBrandPreview";

const MAX_LOGO_BYTES = 512 * 1024;

// Iter 245 — drag a logo straight onto the page (or click to browse). The file
// is read locally into a data URL and handed back at once, so every preview on
// the page (header + sign-in) updates before anything is saved.
export function LogoDropZone({ src, onFile, onClear, disabled, label, testid,
                              dark = false, hint, className = "" }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const take = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("That file isn't an image"); return; }
    if (file.size > MAX_LOGO_BYTES) { toast.error("Logo must be under 512 KB"); return; }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => { setBusy(false); onFile(reader.result, file); };
    reader.onerror = () => { setBusy(false); toast.error("Could not read that file"); };
    reader.readAsDataURL(file);
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && <Label className="text-xs">{label}</Label>}
      <div
        data-testid={testid}
        onDragEnter={(e) => { stop(e); if (!disabled) setOver(true); }}
        onDragOver={(e) => { stop(e); if (!disabled) setOver(true); }}
        onDragLeave={(e) => { stop(e); setOver(false); }}
        onDrop={(e) => { stop(e); setOver(false); if (!disabled) take(e.dataTransfer?.files?.[0]); }}
        onClick={() => !disabled && inputRef.current?.click()}
        className={[
          "group flex cursor-pointer items-center gap-3 rounded-lg border border-dashed p-3 transition-colors",
          over ? "border-primary bg-primary/5" : "border-slate-300 hover:border-primary/60 hover:bg-slate-50",
          disabled ? "pointer-events-none opacity-60" : "",
        ].join(" ")}>
        <div className={`flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded border ${dark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
          {busy
            ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            : src
              ? <img src={src} alt={label || "logo"} className="h-full w-full object-contain" data-testid={`${testid}-preview`} />
              : <ImageIcon className={`h-5 w-5 ${dark ? "text-slate-600" : "text-slate-300"}`} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 group-hover:text-primary">
            <UploadCloud className="h-3.5 w-3.5" />
            {over ? "Drop to upload" : src ? "Drop a new file or click to replace" : "Drop a logo here or click to browse"}
          </div>
          <p className="truncate text-[11px] text-slate-400">{hint || "PNG or SVG, under 512 KB"}</p>
        </div>
        {src && !disabled && (
          <button type="button" data-testid={`${testid}-remove`}
                  onClick={(e) => { stop(e); onClear(); }}
                  className="shrink-0 text-xs text-red-500 hover:underline">Remove</button>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
               data-testid={`${testid}-input`}
               onChange={(e) => { take(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
}

export function brandingFormOf(org) {
  const b = org?.branding || {};
  return {
    enabled: !!b.enabled,
    brand_name: b.brand_name || org?.name || "",
    brand_subtitle: b.brand_subtitle || "",
    primary_color: b.primary_color || "#3B82F6",
    logo_url: b.logo_url || "",
    logo_url_light: b.logo_url_light || "",
    logo_url_dark: b.logo_url_dark || "",
    support_escalations: b.support_escalations || "",
  };
}

export function PartnerBrandingForm({ value, onChange, partnerName, disabled = false,
                                      showEscalationCopy = true, columns = 1 }) {
  const form = value || {};
  const patch = (p) => onChange({ ...form, ...p });

  const openLoginPreview = () => {
    const payload = {
      brand_name: form.brand_name || partnerName || "",
      brand_subtitle: form.brand_subtitle || "",
      primary_color: form.primary_color || "",
      logo_url: form.logo_url || "",
      logo_url_light: form.logo_url_light || "",
      logo_url_dark: form.logo_url_dark || "",
    };
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    window.open(`/login?brand_preview=${encodeURIComponent(encoded)}`, "_blank", "noopener");
  };

  // Iter 248 — same sectioned layout as the platform Branding tab:
  // logos on the left, identity fields on the right.
  const logos = (
    <div className="space-y-4" data-testid="branding-logo-fields">
      <div className="space-y-1.5">
        <Label>Logo <span className="text-xs font-normal text-slate-400">(legacy · single upload used on both light &amp; dark)</span></Label>
        <LogoDropZone src={form.logo_url} disabled={disabled} testid="branding-logo-drop"
                      onFile={(data) => patch({ logo_url: data })}
                      onClear={() => patch({ logo_url: "" })}
                      hint="PNG/SVG, under 512 KB. Used everywhere unless a theme variant below is set." />
        <p className="text-xs text-slate-400">
          Also shown on the co-branded Partner QBR PDF cover. No logo = your company name is shown instead.
        </p>
      </div>
      <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="branding-theme-logos">
        <div>
          <div className="text-sm font-semibold text-slate-800">Theme-specific logos</div>
          <p className="text-xs text-slate-500">Upload a light-mode logo (dark text, sits on white) AND a dark-mode logo (light text, sits on the dark chrome). Each under 512 KB. When either is set it takes precedence over the legacy logo above.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LogoDropZone src={form.logo_url_light} disabled={disabled} label="Light-mode logo"
                        testid="branding-logo-light" hint="Dark wordmark on transparent"
                        onFile={(data) => patch({ logo_url_light: data })}
                        onClear={() => patch({ logo_url_light: "" })} />
          <LogoDropZone src={form.logo_url_dark} disabled={disabled} label="Dark-mode logo" dark
                        testid="branding-logo-dark" hint="Light wordmark on transparent"
                        onFile={(data) => patch({ logo_url_dark: data })}
                        onClear={() => patch({ logo_url_dark: "" })} />
        </div>
        <p className="text-[11px] text-slate-400">Sizing tip: aim for ~30–40px tall assets with a transparent background. The top-bar renders at 70px height with object-contain, so wider wordmarks look best.</p>
        <TopBarPreview
          lightSrc={form.logo_url_light || form.logo_url}
          darkSrc={form.logo_url_dark || form.logo_url}
          brandName={form.brand_name || partnerName || "Partner"}
          primaryColor={form.primary_color || "#3B82F6"}
          className="pt-1"
        />
      </div>
    </div>
  );

  const identity = (
    <div className="space-y-4" data-testid="branding-identity-fields">
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
        <div>
          <div className="text-sm font-medium text-slate-700">Enable custom branding</div>
          <div className="text-xs text-slate-400">Applies to this partner's users and their customers after login.</div>
        </div>
        <Switch data-testid="branding-enabled-switch" disabled={disabled} checked={!!form.enabled}
                onCheckedChange={(v) => patch({ enabled: v })} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Brand name</Label>
          <Input data-testid="branding-name-input" disabled={disabled} value={form.brand_name || ""}
                 onChange={(e) => patch({ brand_name: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Subtitle</Label>
          <Input data-testid="branding-subtitle-input" disabled={disabled} value={form.brand_subtitle || ""}
                 onChange={(e) => patch({ brand_subtitle: e.target.value })} placeholder="Admin Dashboard" /></div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Primary color</Label>
          <div className="flex items-center gap-3">
            <input type="color" data-testid="branding-color-input" disabled={disabled}
                   value={form.primary_color || "#3B82F6"}
                   onChange={(e) => patch({ primary_color: e.target.value })}
                   className="h-9 w-14 cursor-pointer rounded border border-slate-200" />
            <Input value={form.primary_color || ""} disabled={disabled}
                   onChange={(e) => patch({ primary_color: e.target.value })} className="flex-1" />
          </div>
        </div>
        <div className="space-y-1.5"><Label>Logo URL</Label>
          <Input data-testid="branding-logo-input"
                 value={form.logo_url?.startsWith("data:") ? "" : (form.logo_url || "")}
                 onChange={(e) => patch({ logo_url: e.target.value })} placeholder="https://…"
                 disabled={disabled || form.logo_url?.startsWith("data:")} /></div>
      </div>
      {showEscalationCopy && (
        <div className="space-y-1.5">
          <Label>Support &amp; Escalations copy</Label>
          <textarea
            data-testid="branding-support-escalations-input"
            disabled={disabled}
            className="min-h-[110px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
            value={form.support_escalations || ""}
            onChange={(e) => patch({ support_escalations: e.target.value })}
            placeholder="Shown on the customer + partner dashboard Support and Escalations card. Empty = use the platform default." />
          <p className="text-xs text-slate-400">
            Referral, Agent and Master Agent tiers always see the platform default regardless of this field.
          </p>
        </div>
      )}
      <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="branding-login-preview-block">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-800">Sign-in page preview</div>
          <Button type="button" variant="outline" size="sm" data-testid="branding-open-login-preview"
                  onClick={openLoginPreview}>
            <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
          </Button>
        </div>
        <LoginBrandPreview brand={form} partnerName={partnerName} />
      </div>
    </div>
  );

  if (columns === 2) {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:gap-8" data-testid="partner-branding-form">
        {logos}
        {identity}
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="partner-branding-form">
      {identity}
      {logos}
    </div>
  );
}
